import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Send,
  CheckCircle2,
  Mail,
  Clock,
  Shield,
  ChevronDown,
  Phone,
  MapPin,
} from "lucide-react";
import { BUSINESS_TYPES } from "../../lib/constants";

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    business: "",
    type: "",
    whatsapp: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    if (error) setError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.business.trim() || !form.type || !form.whatsapp.trim()) {
      setError("Please fill in your name, business, business type, and WhatsApp number.");
      return;
    }

    // Build mailto fallback so the message also lands in inbox
    const subject = encodeURIComponent(`Ping free-trial request — ${form.business}`);
    const body = encodeURIComponent(
      `Name: ${form.name}\nBusiness: ${form.business}\nBusiness type: ${form.type}\nWhatsApp: ${form.whatsapp}\n\nMessage:\n${form.message || "(no message)"}\n\n— Sent from www.pingme.ae`
    );
    // Open mail client in a new tab so the on-page confirmation stays visible
    try {
      window.open(`mailto:tariq@pingme.ae?subject=${subject}&body=${body}`, "_blank");
    } catch (_) {
      // ignore — confirmation still shown
    }

    setSubmitted(true);
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-[#F9FAFB] py-20 sm:py-28 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Left: copy */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] ping-gradient-text mb-4">
              Get in touch
            </p>
            <h2 className="text-4xl sm:text-5xl lg:text-[3.5rem] lg:leading-[1.05] font-black tracking-tight text-[#1A1A2E]">
              Start your <span className="ping-gradient-text">free trial</span>
            </h2>
            <p className="mt-5 text-lg text-gray-600 max-w-md">
              Free 30-day trial. No card required. We will set everything up for
              you.
            </p>

            <div className="mt-10 space-y-5">
              {[
                {
                  icon: Clock,
                  title: "Live in 10 minutes",
                  body: "We get on a call, set you up, and you're sending notifications the same day.",
                },
                {
                  icon: Shield,
                  title: "No commitments",
                  body: "Cancel any time. Your customer database is always yours to export.",
                },
                {
                  icon: Mail,
                  title: "Talk to a human",
                  body: (
                    <>
                      Prefer email?{" "}
                      <a
                        href="mailto:tariq@pingme.ae"
                        className="font-semibold text-[#7B5EA7] hover:text-[#4B9FE8] transition-colors underline-offset-4 hover:underline"
                        data-testid="contact-email-link"
                      >
                        tariq@pingme.ae
                      </a>
                    </>
                  ),
                },
                {
                  icon: Phone,
                  title: "Call or WhatsApp us",
                  body: (
                    <a
                      href="tel:+971581912500"
                      data-testid="contact-phone-link"
                      className="font-semibold text-[#7B5EA7] hover:text-[#4B9FE8] transition-colors underline-offset-4 hover:underline"
                    >
                      +971 58 191 2500
                    </a>
                  ),
                },
                {
                  icon: MapPin,
                  title: "Where we are",
                  body: (
                    <span data-testid="contact-location">
                      Dubai, United Arab Emirates
                    </span>
                  ),
                },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.title} className="flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-xl ping-gradient flex items-center justify-center">
                      <Icon size={18} className="text-white" strokeWidth={2.2} />
                    </div>
                    <div>
                      <div className="font-bold text-[#1A1A2E]">
                        {item.title}
                      </div>
                      <div className="text-[15px] text-gray-600 leading-relaxed">
                        {item.body}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Right: form */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-7 lg:pl-4"
          >
            <div className="relative bg-white border border-gray-200/80 rounded-3xl p-6 sm:p-9 shadow-xl shadow-gray-200/40">
              <AnimatePresence mode="wait">
                {submitted ? (
                  <motion.div
                    key="thanks"
                    data-testid="contact-thank-you"
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                    className="text-center py-8 sm:py-12"
                  >
                    <div className="w-16 h-16 mx-auto rounded-full ping-gradient flex items-center justify-center mb-5 shadow-lg shadow-[#7B5EA7]/30">
                      <CheckCircle2 size={30} className="text-white" strokeWidth={2.3} />
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-black text-[#1A1A2E] tracking-tight mb-3">
                      Thank you, {form.name.split(" ")[0] || "there"}!
                    </h3>
                    <p className="text-gray-600 max-w-md mx-auto leading-relaxed">
                      We've received your request. A member of the Ping team will
                      WhatsApp you within one working day to set up your free
                      trial.
                    </p>
                    <p className="mt-4 text-sm text-gray-500">
                      Didn't see an email draft open?{" "}
                      <a
                        href="mailto:tariq@pingme.ae"
                        className="font-semibold ping-gradient-text hover:underline"
                      >
                        Email us at tariq@pingme.ae
                      </a>
                    </p>
                    <button
                      data-testid="contact-reset"
                      onClick={() => {
                        setForm({ name: "", business: "", type: "", whatsapp: "", message: "" });
                        setSubmitted(false);
                      }}
                      className="mt-7 text-sm font-semibold text-gray-500 hover:text-[#1A1A2E] transition-colors"
                    >
                      Send another request →
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    data-testid="contact-form"
                    onSubmit={handleSubmit}
                    initial={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-4 sm:space-y-5"
                  >
                    <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
                      <Field
                        label="Your name"
                        required
                        testId="contact-name"
                        value={form.name}
                        onChange={handleChange("name")}
                        placeholder="Ahmed Al Mansouri"
                      />
                      <Field
                        label="Business name"
                        required
                        testId="contact-business"
                        value={form.business}
                        onChange={handleChange("business")}
                        placeholder="Al Noor Tailors"
                      />
                    </div>

                    <SelectField
                      label="Business type"
                      required
                      testId="contact-type"
                      value={form.type}
                      onChange={handleChange("type")}
                    />

                    <Field
                      label="WhatsApp number"
                      required
                      type="tel"
                      testId="contact-whatsapp"
                      value={form.whatsapp}
                      onChange={handleChange("whatsapp")}
                      placeholder="+971 50 123 4567"
                    />

                    <div>
                      <label className="block text-sm font-semibold text-[#1A1A2E] mb-2">
                        Message{" "}
                        <span className="text-gray-400 font-normal">
                          (optional)
                        </span>
                      </label>
                      <textarea
                        data-testid="contact-message"
                        rows={4}
                        value={form.message}
                        onChange={handleChange("message")}
                        placeholder="Tell us a little about your business..."
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-[15px] text-[#1A1A2E] placeholder-gray-400 focus:outline-none focus:border-[#7B5EA7] focus:ring-4 focus:ring-[#7B5EA7]/10 transition-all resize-none"
                      />
                    </div>

                    {error && (
                      <p
                        data-testid="contact-error"
                        className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2"
                      >
                        {error}
                      </p>
                    )}

                    <button
                      data-testid="contact-submit"
                      type="submit"
                      className="w-full ping-gradient text-white font-semibold px-6 py-4 rounded-full hover:shadow-xl hover:shadow-[#7B5EA7]/30 hover:scale-[1.01] active:scale-[0.99] transition-all flex items-center justify-center gap-2"
                    >
                      Request Your Free Trial
                      <Send size={17} />
                    </button>

                    <p className="text-xs text-gray-500 text-center leading-relaxed">
                      By submitting, you agree we may contact you on WhatsApp or
                      email about Ping. No spam, ever.
                    </p>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, required, testId, type = "text", value, onChange, placeholder }) => (
  <div>
    <label className="block text-sm font-semibold text-[#1A1A2E] mb-2">
      {label}
      {required && <span className="text-[#7B5EA7] ml-0.5">*</span>}
    </label>
    <input
      data-testid={testId}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-[15px] text-[#1A1A2E] placeholder-gray-400 focus:outline-none focus:border-[#7B5EA7] focus:ring-4 focus:ring-[#7B5EA7]/10 transition-all"
    />
  </div>
);

const SelectField = ({ label, required, testId, value, onChange }) => (
  <div>
    <label className="block text-sm font-semibold text-[#1A1A2E] mb-2">
      {label}
      {required && <span className="text-[#7B5EA7] ml-0.5">*</span>}
    </label>
    <div className="relative">
      <select
        data-testid={testId}
        value={value}
        onChange={onChange}
        required={required}
        className={`w-full appearance-none px-4 py-3 pr-10 rounded-xl border border-gray-200 bg-white text-[15px] focus:outline-none focus:border-[#7B5EA7] focus:ring-4 focus:ring-[#7B5EA7]/10 transition-all ${
          value ? "text-[#1A1A2E]" : "text-gray-400"
        }`}
      >
        <option value="">Select your business type</option>
        {BUSINESS_TYPES.map((t) => (
          <option key={t} value={t} className="text-[#1A1A2E]">
            {t}
          </option>
        ))}
      </select>
      <ChevronDown
        size={18}
        className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
      />
    </div>
  </div>
);

export default Contact;
